import type { TAccountProduct } from '../types';
import type { Merge, PlainObject } from 'simplytyped';
import { last } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import type {
  AccountProductBlockRow,
  ArrayAccountProductBlockRow,
  TTableCellContent
} from '../../components';
import type { ArrayPath, FieldValues } from 'react-hook-form';
import type { ComponentProps } from 'react';
import type { SimpleTableRow } from '../../components/molecules';
import type { EditableTable } from '../../components/organisms/EditableTable';

export const isRowVisible = (row: {
  name: string;
  nonEditable?: boolean;
}): boolean => {
  return !!row.name && !row.nonEditable;
};
export const isArrayRow = (
  row: AccountProductBlockRow
): row is ArrayAccountProductBlockRow => {
  return row.contentType === 'array';
};

export type FeeRowProps<Schema extends FieldValues = FieldValues> = Merge<
  ComponentProps<typeof SimpleTableRow>,
  {
    hidden?: boolean;
    nonEditable?: boolean;
    name: ArrayPath<Schema>;
    contentType: 'table';
    content: Array<Array<TTableCellContent>>;
    tableConfig: Merge<
      Omit<
        ComponentProps<typeof EditableTable>,
        'name' | 'control' | 'register'
      >,
      {
        hideDeleteButton?: boolean;
      }
    >;
  }
>;
export type CellDataCustom = Merge<
  {
    nonEditable?: boolean;
  },
  | {
      contentType: 'range';
      nonEditable?: boolean;
      content: {
        min: number;
        max: number;
      };
    }
  | {
      contentType: 'number' | 'currency' | 'percent' | 'percent*100';
      content: number;
      nonEditable?: boolean;
    }
  | {
      contentType: 'text';
      content: string;
      nonEditable?: boolean;
    }
>;
export const getDefaultRowValue =
  (product: TAccountProduct) =>
  (fields: Array<Record<'id', string>>): Record<'id', string> & PlainObject => {
    const lastRow = last(fields) as unknown as {
      '0': {
        content: {
          min: number;
          max: number;
        };
      };
      id: string;
    };

    const lastRowFirst = lastRow['0'];
    const lastRowFirstMax =
      lastRowFirst.content.max === -1
        ? product.maxLoanAmount
        : lastRowFirst.content.max;
    const lastRowDifference = lastRowFirstMax - lastRowFirst.content.min;
    const min = Math.max(
      Math.round(lastRowFirst.content.min + lastRowDifference / 2),
      product.minLoanAmount
    );
    const usePrevMaxAsNewMax =
      lastRowFirst.content.max !== -1 &&
      lastRowFirst.content.max > product.maxLoanAmount;

    return {
      ...lastRow,
      '0': {
        ...lastRowFirst,
        content: {
          ...lastRowFirst.content,
          min,
          max: usePrevMaxAsNewMax
            ? lastRowFirst.content.max
            : product.maxLoanAmount
        }
      },
      added: 'true',
      id: uuidv4()
    };
  };
